import { DayHours, WeeklyHours, TemporaryClosure } from '../services/storeHoursApiService.ts';

/**
 * Get a date string in YYYY-MM-DD format using LOCAL date components
 * This avoids timezone issues that occur with toISOString() which uses UTC
 */
const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Convert 24hr format back to display format like "Mon: 10am — 8pm"
export const formatTimeToDisplay = (hour: string): string => {
  const hourNum = parseInt(hour.split(':')[0]);
  
  if (hourNum === 0) return '12am';
  if (hourNum < 12) return `${hourNum}am`;
  if (hourNum === 12) return '12pm';
  return `${hourNum - 12}pm`;
};

// Check if a specific date has an approved temporary closure within the next week
const getClosureForDate = (targetDate: Date, temporaryClosures: TemporaryClosure[]): TemporaryClosure | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

  const targetDateCopy = new Date(targetDate);
  targetDateCopy.setHours(0, 0, 0, 0); // Reset to start of day

  // Use local date components to avoid UTC conversion issues
  const targetDateString = getLocalDateString(targetDateCopy);

  // Check if target date is within the next 7 days (including today)
  const daysDiff = Math.ceil((targetDateCopy.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isWithinWeek = daysDiff >= 0 && daysDiff < 7;

  if (!isWithinWeek) {
    return null;
  }

  // Find matching closure for this exact date
  const foundClosure = temporaryClosures.find(closure => {
    const closureDate = new Date(closure.date);
    // Use local date components to avoid UTC conversion issues
    const closureDateString = getLocalDateString(closureDate);

    const matches = closureDateString === targetDateString && closure.approved;

    return matches;
  }) || null;

  return foundClosure;
};

export const formatDayHours = (
  dayName: string,
  dayHours: DayHours,
  temporaryClosures?: TemporaryClosure[],
  targetDate?: Date
): string => {
  // Check if this specific date has a temporary closure within the next week
  if (temporaryClosures && targetDate) {
    const closure = getClosureForDate(targetDate, temporaryClosures);
    if (closure) {
      // Check if this closure has special hours (modified hours instead of fully closed)
      if (closure.specialHours) {
        const openTime = formatTimeToDisplay(closure.specialHours.open);
        const closeTime = formatTimeToDisplay(closure.specialHours.close);
        return `${dayName}: ${openTime} — ${closeTime} (${closure.reason})`;
      }
      return `${dayName}: Closed (${closure.reason})`;
    }
  }
  
  if (dayHours.closed) {
    return `${dayName}: Closed`;
  }
  
  const openTime = formatTimeToDisplay(dayHours.open);
  const closeTime = formatTimeToDisplay(dayHours.close);
  
  return `${dayName}: ${openTime} — ${closeTime}`;
};

export const formatWeeklyHours = (hours: WeeklyHours, temporaryClosures?: TemporaryClosure[]) => {
  const now = new Date();
  const today = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate the date for each day of the current week
  const getDateForDayOfWeek = (dayOfWeek: number): Date => {
    const date = new Date(now);
    const currentDay = date.getDay();
    const diff = dayOfWeek - currentDay;
    date.setDate(date.getDate() + diff);
    return date;
  };
  
  const mondayDate = getDateForDayOfWeek(1);
  const tuesdayDate = getDateForDayOfWeek(2);
  const wednesdayDate = getDateForDayOfWeek(3);
  const thursdayDate = getDateForDayOfWeek(4);
  const fridayDate = getDateForDayOfWeek(5);
  const saturdayDate = getDateForDayOfWeek(6);
  const sundayDate = getDateForDayOfWeek(0);
  
  return {
    monHours: formatDayHours(today === 1 ? 'Today' : 'Mon', hours.monday, temporaryClosures, mondayDate),
    tuesHours: formatDayHours(today === 2 ? 'Today' : 'Tues', hours.tuesday, temporaryClosures, tuesdayDate),
    wedHours: formatDayHours(today === 3 ? 'Today' : 'Weds', hours.wednesday, temporaryClosures, wednesdayDate),
    thursHours: formatDayHours(today === 4 ? 'Today' : 'Thurs', hours.thursday, temporaryClosures, thursdayDate),
    friHours: formatDayHours(today === 5 ? 'Today' : 'Fri', hours.friday, temporaryClosures, fridayDate),
    satHours: formatDayHours(today === 6 ? 'Today' : 'Sat', hours.saturday, temporaryClosures, saturdayDate),
    sunHours: formatDayHours(today === 0 ? 'Today' : 'Sun', hours.sunday, temporaryClosures, sundayDate)
  };
};